import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import { 
  Shield, AlertTriangle, CheckCircle, Info, Loader2, HelpCircle, Save
} from 'lucide-react';
import api from '../utils/api';

const DisciplinaryHistory = () => {
  const [record, setRecord] = useState(null);
  const [guidance, setGuidance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    has_disciplinary_history: false,
    school_discipline: false,
    school_discipline_explanation: '',
    criminal_history: false,
    criminal_explanation: '',
    academic_probation: false,
    academic_explanation: '',
    additional_info: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [recordRes, guidanceRes] = await Promise.all([
        api.get('/disciplinary/'),
        api.get('/disciplinary/guidance')
      ]);

      if (recordRes.data.exists) {
        setRecord(recordRes.data.record);
        setFormData(recordRes.data.record);
      } else {
        setRecord(recordRes.data);
      }
      setGuidance(guidanceRes.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/disciplinary/', formData);
      toast({ title: 'Success', description: 'Disciplinary history saved successfully' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Disciplinary History</h1>
          <p className="text-gray-600">Complete your disciplinary disclosure for college applications</p>
        </div>

        <Tabs defaultValue="form">
          <TabsList className="mb-6">
            <TabsTrigger value="form">Disclosure Form</TabsTrigger>
            <TabsTrigger value="guidance">Guidance</TabsTrigger>
          </TabsList>

          <TabsContent value="form">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Disciplinary Disclosure
                </CardTitle>
                <CardDescription>
                  Answer honestly - many students with disciplinary history are still admitted
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Main question */}
                <div className="bg-amber-50 p-4 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Checkbox 
                      id="has_history"
                      checked={formData.has_disciplinary_history}
                      onCheckedChange={(checked) => setFormData({...formData, has_disciplinary_history: checked})}
                    />
                    <div>
                      <Label htmlFor="has_history" className="font-medium">
                        I have disciplinary history to report
                      </Label>
                      <p className="text-sm text-gray-600 mt-1">
                        Check this if you have any school discipline, criminal history, or academic probation to disclose
                      </p>
                    </div>
                  </div>
                </div>

                {formData.has_disciplinary_history && (
                  <div className="space-y-6">
                    {/* School Discipline */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-start space-x-3 mb-4">
                        <Checkbox 
                          id="school_discipline"
                          checked={formData.school_discipline}
                          onCheckedChange={(checked) => setFormData({...formData, school_discipline: checked})}
                        />
                        <Label htmlFor="school_discipline" className="font-medium">
                          School Disciplinary Action (suspension, expulsion, probation)
                        </Label>
                      </div>
                      {formData.school_discipline && (
                        <Textarea 
                          placeholder="Describe what happened, when it occurred, and what you learned from the experience..."
                          value={formData.school_discipline_explanation}
                          onChange={(e) => setFormData({...formData, school_discipline_explanation: e.target.value})}
                          rows={4}
                        />
                      )}
                    </div>

                    {/* Criminal History */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-start space-x-3 mb-4">
                        <Checkbox 
                          id="criminal_history"
                          checked={formData.criminal_history}
                          onCheckedChange={(checked) => setFormData({...formData, criminal_history: checked})}
                        />
                        <Label htmlFor="criminal_history" className="font-medium">
                          Criminal History (charges, convictions)
                        </Label>
                      </div>
                      {formData.criminal_history && (
                        <Textarea 
                          placeholder="Describe the incident, any legal outcomes, and steps you've taken since..."
                          value={formData.criminal_explanation}
                          onChange={(e) => setFormData({...formData, criminal_explanation: e.target.value})}
                          rows={4}
                        />
                      )}
                    </div>

                    {/* Academic Probation */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-start space-x-3 mb-4">
                        <Checkbox 
                          id="academic_probation"
                          checked={formData.academic_probation}
                          onCheckedChange={(checked) => setFormData({...formData, academic_probation: checked})}
                        />
                        <Label htmlFor="academic_probation" className="font-medium">
                          Academic Probation or Dismissal (including academic dishonesty)
                        </Label>
                      </div>
                      {formData.academic_probation && (
                        <Textarea 
                          placeholder="Explain the circumstances and how you've improved academically since..."
                          value={formData.academic_explanation}
                          onChange={(e) => setFormData({...formData, academic_explanation: e.target.value})}
                          rows={4}
                        />
                      )}
                    </div>

                    {/* Additional Info */}
                    <div>
                      <Label>Additional Information (Optional)</Label>
                      <Textarea 
                        placeholder="Any other context you'd like to provide..."
                        value={formData.additional_info}
                        onChange={(e) => setFormData({...formData, additional_info: e.target.value})}
                        rows={3}
                        className="mt-2"
                      />
                    </div>
                  </div>
                )}

                {!formData.has_disciplinary_history && (
                  <div className="bg-green-50 p-4 rounded-lg flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-green-800">No disciplinary history to report</span>
                  </div>
                )}

                <Button onClick={handleSave} disabled={saving} className="w-full">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Disciplinary History
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="guidance">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="h-5 w-5" />
                    General Guidance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {guidance?.general_guidance?.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>School Discipline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-red-600 mb-2">What to Report</h4>
                      <ul className="space-y-1 text-sm">
                        {guidance?.school_discipline?.what_to_report?.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-600 mb-2">What NOT to Report</h4>
                      <ul className="space-y-1 text-sm">
                        {guidance?.school_discipline?.what_not_to_report?.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Writing Tips</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {guidance?.writing_tips?.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <HelpCircle className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DisciplinaryHistory;
